
import React, { useState, useMemo, useEffect } from 'react';
import { Menu, MenuItem, Restaurant, SocialLinks, DeliveryServices } from '@/types/menu';
import SimpleMenuHeader from '../layout/SimpleMenuHeader';
import SimpleMenuFooter from '../layout/SimpleMenuFooter';
import MenuInfo from '../layout/MenuInfo';
import MenuContent from './MenuContent';
import ScrollToTopBottom from '../navigation/ScrollToTopBottom';
import EventsBanner from '../features/EventsBanner';
import NewsletterSignup from '../features/NewsletterSignup';
import MenuBottomNavbar from '../features/MenuBottomNavbar';
import { useHappyHour } from '@/hooks/useHappyHour';
import { supabase } from '@/integrations/supabase/client';
import { parseJsonField } from '@/utils/menuItemUtils';
import { MenuLanguageProvider } from '@/contexts/MenuLanguageContext';
import { LanguageCode } from '@/constants/menuUITranslations';

interface SimpleMenuViewProps {
  menu: Menu;
  menuItems: MenuItem[];
}

const SimpleMenuView: React.FC<SimpleMenuViewProps> = ({ 
  menu, 
  menuItems
}) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>('fr');
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [restaurantNotFound, setRestaurantNotFound] = useState(false);
  
  // Check for available translations
  const availableLanguages = useMemo<LanguageCode[]>(() => {
    const langs = new Set<LanguageCode>(['fr']);
    menuItems.forEach(item => {
      if (item.translations && typeof item.translations === 'object') {
        Object.keys(item.translations).forEach(lang => langs.add(lang as LanguageCode));
      }
    });
    return Array.from(langs);
  }, [menuItems]);

  const { isHappyHour, setIsHappyHour } = useHappyHour(
    menu?.custom_css?.happyHourSchedule
  );
  
  const categories = useMemo(() => {
    if (!menuItems || menuItems.length === 0) return [];
    const uniqueCategories = new Set<string>();
    menuItems.forEach(item => {
      if (item.category && !item.hidden && typeof item.category === 'string') {
        uniqueCategories.add(item.category);
      }
    });
    const categoriesArray = Array.from(uniqueCategories);
    if (!activeCategory && categoriesArray.length > 0) {
      setActiveCategory(categoriesArray[0]);
    }
    return categoriesArray;
  }, [menuItems, activeCategory]);

  // Fetch the full restaurant object
  useEffect(() => {
    const fetchRestaurant = async () => {
      if (menu.restaurant_id) {
        try {
          const { data, error } = await supabase
            .from('restaurants')
            .select('*')
            .eq('id', menu.restaurant_id)
            .maybeSingle();
            
          if (error) {
            setRestaurantNotFound(true);
            return;
          }
            
          if (data) {
            const socialLinks = parseJsonField<SocialLinks>(data.social_links, {});
            const deliveryServices = parseJsonField<DeliveryServices>(data.delivery_services, {});
            
            setRestaurant({
              id: data.id,
              user_id: data.user_id,
              name: data.name,
              address: data.address,
              description: data.description,
              phone: data.phone || undefined,
              email: data.email || undefined,
              website: data.website || undefined,
              type: data.type,
              banner_url: data.banner_url || undefined,
              social_links: socialLinks,
              delivery_services: deliveryServices,
              created_at: data.created_at,
              updated_at: data.updated_at
            });
            setRestaurantNotFound(false);
          } else {
            setRestaurantNotFound(true);
          }
        } catch (err) {
          setRestaurantNotFound(true);
        }
      } else {
        setRestaurant(null);
        setRestaurantNotFound(false);
      }
    };
    fetchRestaurant();
  }, [menu.restaurant_id]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    const element = document.getElementById(`category-${category}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <div 
      className="flex flex-col min-h-screen bg-white pb-16"
      style={{ 
        fontFamily: menu.font_family || 'Inter, sans-serif',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}
    >
      <div className="w-full mx-auto lg:max-w-6xl xl:max-w-7xl">
        <SimpleMenuHeader 
          menu={menu}
          currentLanguage={currentLanguage}
          onLanguageChange={setCurrentLanguage}
          availableLanguages={availableLanguages}
        />
        
        <div className="lg:px-8 xl:px-12">
          <MenuInfo 
            menu={menu}
            restaurantName={restaurant?.name}
            restaurantType={restaurant?.type}
            restaurantAddress={restaurant?.address}
            restaurantPhone={restaurant?.phone}
            restaurantDescription={restaurant?.description}
            currentLanguage={currentLanguage}
            restaurantId={menu.restaurant_id || undefined}
          />
          
          <MenuContent 
            menu={menu}
            menuItems={menuItems}
            categories={categories}
            activeCategory={activeCategory}
            isHappyHour={isHappyHour}
            setIsHappyHour={setIsHappyHour}
            onCategoryChange={handleCategoryChange}
            currentLanguage={currentLanguage}
          />
          
          {menu.restaurant_id && (
            <NewsletterSignup
              restaurantId={menu.restaurant_id}
              restaurantName={restaurant?.name}
              primaryColor={menu.primary_color || undefined}
              currentLanguage={currentLanguage}
            />
          )}

          {/* Events at the bottom */}
          {menu.restaurant_id && (
            <div id="events-section">
              <EventsBanner 
                restaurantId={menu.restaurant_id} 
                primaryColor={menu.primary_color || undefined}
                restaurantName={restaurant?.name}
                currentLanguage={currentLanguage}
              />
            </div>
          )}
        </div>

        {restaurant && !restaurantNotFound && (
          <div className="lg:px-8 xl:px-12">
            <SimpleMenuFooter
              restaurant={restaurant}
              currentLanguage={currentLanguage}
            />
          </div>
        )}
      </div>
      
      {/* Bottom navigation bar */}
      {menu.restaurant_id && (
        <MenuBottomNavbar
          restaurantId={menu.restaurant_id}
          primaryColor={menu.primary_color || '#8B5CF6'}
          restaurantName={restaurant?.name || menu.name}
        />
      )}
      
      <ScrollToTopBottom primaryColor={menu.primary_color} />
    </div>
  );
};

export default SimpleMenuView;
